import { useAIStore } from 'src/store/ai';
import { useAuthStore } from 'src/store/auth';
import { useInsuranceStore } from 'src/store/insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { 
  EstimationRequest, 
  InsuranceChatRequest, 
  InsuranceChatHook,
  ShowPlansParams,
  ComparePlansParams,
  UpdateApplicantsParams,
  UpdateApplicantParams
} from 'src/services/interfaces/ai';
import { useEffect } from 'react';
import { InsurancePlan } from 'src/services/interfaces/insurance';
import { format, parseISO } from 'date-fns';
import { useCompareStore } from 'src/store/compare';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';

export const useAI = () => {
  const { user } = useAuthStore();
  const plans = useInsuranceStore((state) => state.plans);
  const { data, setData, resetAllForms, setActiveDentalVisionCategory } = useOnboardingStore();
  const setAiHighlightedPlan = useOnboardingStore((s) => s.setAiHighlightedPlan);
  const { handleJumpTo } = useFormControls();
  const {
    // Estimation state
    estimationLoading,
    estimationResult,
    estimationError,

    // Chat state
    chatLoading,
    chatSessionId,
    chatHistory,
    chatError,

    // Actions
    runEstimation,
    sendChatMessage,
    generateNewSessionId,
    clearEstimation,
    clearChat,
    clearErrors,
  } = useAIStore();
  const applicationData = transformApplicationData(data, plans)
  const { open, setSelectedPlans, setCompareViewCategory } = useCompareStore();

  // Helper functions
  const sendMessage = async (message: string) => {
    const chatData: InsuranceChatRequest = {
      message,
      session_id: chatSessionId || undefined, // Pass session_id if exists, otherwise let store generate it
    };
    if (user?.id) {
      chatData.userId = +user?.id;
    }

    await sendChatMessage(chatData, applicationData);
  };

  const startNewChat = () => {
    generateNewSessionId();
    clearChat();
  };

  // Функция для обработки команды show_plans
  const handleShowPlans = (planIds: number[]) => {
    if (!planIds || planIds.length === 0) return;

    // Берем первый план из списка
    const planId = planIds[0];
    const plan = plans.find(p => p.id === planId);

    if (!plan) {
      console.warn(`Plan with id ${planId} not found`);
      return;
    }

    // Определяем категорию плана и перенаправляем
    const category = plan.category?.toLowerCase();
    let sectionName = '';

    switch (category) {
      case 'major medical':
        sectionName = 'Health Plans';
        break;
      case 'dental':
        sectionName = 'Dental and Vision';
        break;
      case 'vision':
        sectionName = 'Vision Plans';
        break;
      case 'accident':
        sectionName = 'Accident Plans';
        break;
      case 'critical illness':
        sectionName = 'Critical Illness Plans';
        break;
      case 'hospital indemnity':
        sectionName = 'Hospital Indemnity Plans';
        break;
      default:
        console.warn(`Unknown plan category: ${category}`);
    }

    // Подсветим план в UI
    setAiHighlightedPlan(plan.uid);

    // Перенаправляем на нужную страницу
    handleJumpTo(sectionName);

    console.log(`Redirecting to ${sectionName} for plan:`, plan);

    // Для dental/vision планов устанавливаем активную категорию
    if (category !== 'major medical') {
      // Получаем все доступные категории планов (кроме Major Medical)
      const availableCategories = plans
        .filter(plan => plan.category && plan.category !== 'Major Medical')
        .map(plan => plan.category!)
        .filter((category, index, arr) => arr.indexOf(category) === index); // убираем дубликаты

      // Ставим Dental на первое место, если он есть
      const dentalExists = availableCategories.includes('Dental');
      const orderedCategories = dentalExists 
        ? ['Dental', ...availableCategories.filter(c => c !== 'Dental')]
        : availableCategories;

      // Находим индекс категории
      const categoryIndex = orderedCategories.findIndex(cat => 
        cat.toLowerCase() === category
      );

      if (categoryIndex !== -1) {
        // alert(categoryIndex)
        setActiveDentalVisionCategory(categoryIndex);
      }
    }
  };

  // Функция для обработки команды compare_plans
  const handleComparePlans = (planIds: number[]) => {
    if (!planIds || planIds.length < 2) {
      console.warn('Need at least 2 plans to compare');
      return;
    }

    // Находим планы по ID
    const plansToCompare = planIds
      .map(id => plans.find(p => p.id === id))
      .filter((p): p is InsurancePlan => !!p);

    if (plansToCompare.length !== planIds.length) {
      console.warn('Some plans not found for comparison');
      return;
    }

    // TODO 
    // Определяем категорию по первому плану
    const firstPlan = plansToCompare[0];
    const category = firstPlan.category?.toLowerCase();
    console.log('firstPlan', category, firstPlan);
    let sectionName = '';
    let compareCategory = '';

    switch (category) {
      case 'major medical':
        sectionName = 'Health Plans';
        compareCategory = 'Major Medical';
        break;
      case 'dental':
        sectionName = 'Dental and Vision';
        compareCategory = 'Dental';
        break;
      case 'vision':
        sectionName = 'Vision Plans';
        compareCategory = 'Vision';
        break;
      case 'accident':
        sectionName = 'Accident Plans';
        compareCategory = 'Accident';
        break;
      case 'critical illness':
        sectionName = 'Critical Illness Plans';
        compareCategory = 'Critical Illness';
        break;
      case 'hospital indemnity':
        sectionName = 'Hospital Indemnity Plans';
        compareCategory = 'Hospital Indemnity';
        break;
      default:
        console.warn(`Unknown plan category: ${category}`);
        return;
    }

    // Проверяем, что все планы принадлежат одной категории
    const categories = [...new Set(plansToCompare.map(p => p.category?.toLowerCase()))];
    if (categories.length > 1) {
      console.warn('Cannot compare plans from different categories');
      return;
    }

    // Устанавливаем планы для сравнения
    setSelectedPlans(plansToCompare.map(p => p.uid));
    // Устанавливаем категорию для сравнения
    setCompareViewCategory(compareCategory);
    // Открываем модальное окно сравнения
    open();

    // Для dental/vision планов устанавливаем активную категорию
    if (category === 'dental' || category === 'vision') {
      // Получаем все доступные категории планов (кроме Major Medical)
      const availableCategories = plans
        .filter(plan => plan.category && plan.category !== 'Major Medical')
        .map(plan => plan.category!)
        .filter((category, index, arr) => arr.indexOf(category) === index); // убираем дубликаты

      // Ставим Dental на первое место, если он есть
      const dentalExists = availableCategories.includes('Dental');
      const orderedCategories = dentalExists 
        ? ['Dental', ...availableCategories.filter(c => c !== 'Dental')]
        : availableCategories;

      // Находим индекс категории
      const categoryIndex = orderedCategories.findIndex(cat => 
        cat.toLowerCase() === category
      );

      if (categoryIndex !== -1) {
        setActiveDentalVisionCategory(categoryIndex);
      }
    }

    // Перенаправляем на нужную страницу
    handleJumpTo(sectionName);
    console.log(`Redirecting to ${sectionName} for plan comparison:`, plansToCompare);
  };

  // Функция для обработки команды update_applicants
  const handleUpdateApplicants = (applicantsData: UpdateApplicantParams[]) => {
    if (!applicantsData || applicantsData.length === 0) {
      console.warn('No applicant data provided');
      return;
    }

    // Получаем текущих заявителей
    const currentApplicants = data.applicants || [];
    const updatedApplicants = [...currentApplicants];

    // Обновляем каждого заявителя
    applicantsData.forEach(applicantUpdate => {
      const { applicant: applicantIndex, ...updateFields } = applicantUpdate;
      
      if (applicantIndex < 0 || applicantIndex >= updatedApplicants.length) {
        console.warn(`Invalid applicant index: ${applicantIndex}`);
        return;
      }

      const currentApplicant = updatedApplicants[applicantIndex];

      // Обновляем только не-null поля
      Object.entries(updateFields).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          // Обрабатываем специальные поля
          if (key === 'dob' && typeof value === 'string') {
            // Преобразуем дату в формат, используемый в приложении
            const date = parseISO(format(new Date(value), 'yyyy-MM-dd'));
            currentApplicant.dateOfBirth = {
              month: (date.getMonth() + 1).toString().padStart(2, '0'),
              day: date.getDate().toString().padStart(2, '0'),
              year: date.getFullYear().toString()
            } as any;
          } else if (key === 'heightFt' || key === 'heightIn') {
            // Обновляем рост
            if (!currentApplicant.height) {
              currentApplicant.height = { ft: 0, in: 0 } as any;
            }
            if (key === 'heightFt') {
              (currentApplicant.height as any).ft = value as number;
            } else if (key === 'heightIn') {
              (currentApplicant.height as any)['in'] = value as number;
            }
          } else {
            // Обновляем остальные поля напрямую
            (currentApplicant as any)[key] = value;
          }
        }
      });
    });

    // Обновляем данные в store
    const updatedData = {
      ...data,
      applicants: updatedApplicants
    };

    setData(updatedData);

    // Обновляем все зарегистрированные формы
    resetAllForms(updatedData);

    console.log('Applicants updated:', updatedApplicants);
  };

  const isEstimationAccepted = estimationResult?.status === 'accepted';
  const isEstimationRejected = estimationResult?.status === 'rejected';

  // Обработка хуков из AI ответов
  useEffect(() => {
    const handleAIHooks = (event: CustomEvent) => {
      const { hooks } = event.detail;

      hooks.forEach((hook: InsuranceChatHook) => {
        switch (hook.tool) {
          case 'show_plans':
            const showPlansParams = hook.params as ShowPlansParams;
            if (showPlansParams?.plans && Array.isArray(showPlansParams.plans)) {
              handleShowPlans(showPlansParams.plans);
            }
            break;
          case 'compare_plans':
            const comparePlansParams = hook.params as ComparePlansParams;
            if (comparePlansParams?.plans && Array.isArray(comparePlansParams.plans)) {
              handleComparePlans(comparePlansParams.plans);
            }
            break;
          case 'update_applicants':
            const updateApplicantsParams = hook.params as UpdateApplicantsParams;
            if (updateApplicantsParams?.applicants && Array.isArray(updateApplicantsParams.applicants)) {
              handleUpdateApplicants(updateApplicantsParams.applicants as UpdateApplicantParams[]);
            }
            break;
          default:
            console.warn('Unknown hook type:', hook.tool);
        }
      });
    };

    // Слушаем события хуков
    window.addEventListener('ai-hooks', handleAIHooks as EventListener);

    return () => {
      window.removeEventListener('ai-hooks', handleAIHooks as EventListener);
    };
  }, [plans, handleJumpTo, data, setData]);

  return {
    // Estimation
    estimationLoading,
    estimationResult,
    estimationError,
    isEstimationAccepted,
    isEstimationRejected,
    runEstimation,
    clearEstimation,

    // Chat
    chatLoading,
    chatSessionId,
    chatHistory,
    chatError,
    sendMessage,
    startNewChat,
    generateNewSessionId,
    clearChat,

    // Plan navigation
    handleShowPlans,
    handleComparePlans,

    // Applicant management
    handleUpdateApplicants,

    // General
    clearErrors,
  };
};
